import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchEarnings } from "@lawyer/services/lawyerApi";
import { Wallet, TrendingUp, Clock, IndianRupee } from "lucide-react";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

function StatCard({ label, value, hint, accent = "navy", testid }) {
  const accents = {
    navy: "bg-[#0A2540] text-white",
    gold: "bg-[#FBF4DF] text-[#0A2540] border border-[#F1E3B5]",
    emerald: "bg-emerald-50 text-emerald-900 border border-emerald-200",
    slate: "bg-slate-50 text-slate-900 border border-slate-200",
  }[accent];
  return (
    <div className={`rounded-2xl p-5 ${accents}`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-widest opacity-70 font-bold">{label}</p>
      <p className="mt-2 text-3xl font-extrabold" style={{ fontFamily: "Poppins" }}>{value}</p>
      {hint && <p className="mt-1 text-xs opacity-80">{hint}</p>}
    </div>
  );
}

export default function LawyerEarnings() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchEarnings({ limit: 50 }).then(setData).catch((e) =>
      toast.error(e?.response?.data?.detail || "Could not load earnings"));
  }, []);

  if (!data) return <div className="text-slate-500" data-testid="lawyer-earnings-loading">Loading…</div>;

  const { totals, items, commission_pct } = data;

  return (
    <>
      <div className="mb-6" data-testid="lawyer-earnings-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Earnings</h1>
        <p className="mt-1 text-sm text-slate-500">
          Net of {commission_pct}% platform commission. Settlements are processed weekly to your registered bank account.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard testid="earn-net-settled" label="Net settled" value={fmt(totals.net_settled)} hint={`From ${totals.completed_count} completed`} accent="navy" />
        <StatCard testid="earn-net-pending" label="Net pending" value={fmt(totals.net_pending)} hint="Settles after completion" accent="gold" />
        <StatCard testid="earn-gross" label="Gross paid" value={fmt(totals.gross_paid)} hint={`${totals.paid_count} paid bookings`} accent="emerald" />
        <StatCard testid="earn-commission" label="Platform commission" value={fmt(totals.platform_commission)} hint={`${commission_pct}% of gross`} accent="slate" />
      </div>

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3 flex items-center gap-1">
        <Wallet size={12} /> Per-consultation breakdown
      </h2>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="earnings-table">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No paid consultations yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  {["When", "Client", "Type", "Gross", "Commission", "Net", "Payout"].map((h) => (
                    <th key={h} className="text-left font-semibold px-4 py-3 whitespace-nowrap text-xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-xs text-slate-500">{(it.completed_at || it.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 font-bold text-[#0A2540]">{it.user_name || "Client"}</td>
                    <td className="px-4 py-3 text-xs uppercase">{it.consultation_type}</td>
                    <td className="px-4 py-3 font-bold">{fmt(it.amount)}</td>
                    <td className="px-4 py-3 text-rose-700">−{fmt(it.commission)}</td>
                    <td className="px-4 py-3 font-extrabold text-emerald-700">{fmt(it.net_earnings)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${
                        it.payout_status === "settled" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                      }`}>
                        {it.payout_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#FBF4DF] grid place-items-center text-[#0A2540]"><TrendingUp size={18} /></div>
          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Tip</p>
            <p className="mt-1 text-sm text-slate-700">Lawyers who go online for at least 3 hours/day on Advoset see ~2.4× more bookings on average.</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-50 grid place-items-center text-emerald-700"><IndianRupee size={18} /></div>
          <div>
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Payout history</p>
            <p className="mt-1 text-sm text-slate-700">Razorpay payout integration arrives in Phase 2. For now, settlements are processed manually by Finance every Friday.</p>
          </div>
        </div>
      </div>
    </>
  );
}
